import type { Patient, Unit } from "@virtual-care/shared";
import { requireEnv } from "@virtual-care/shared";
import { authenticatedFetch } from "../utils/auth";

export class PatientApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = requireEnv(process.env.VUE_APP_API_BASE_URL, "VUE_APP_API_BASE_URL");
  }

  async getUnitsByFacility(facilityId: string): Promise<Unit[]> {
    const response = await authenticatedFetch(`${this.baseUrl}/api/units?facilityId=${facilityId}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch units: ${response.statusText}`);
    }

    return response.json();
  }

  async getPatientsByUnit(unitId: string): Promise<Patient[]> {
    const response = await authenticatedFetch(`${this.baseUrl}/api/patients?unitId=${unitId}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch patients: ${response.statusText}`);
    }

    return response.json();
  }

  async getPatient(patientId: string): Promise<Patient> {
    const response = await authenticatedFetch(`${this.baseUrl}/api/patients/${patientId}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch patient: ${response.statusText}`);
    }

    return response.json();
  }

  async getPatientsByStatus(status: string): Promise<Patient[]> {
    const response = await authenticatedFetch(`${this.baseUrl}/api/patients?status=${status}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch patients by status: ${response.statusText}`);
    }

    return response.json();
  }

  async createPatient(patient: Partial<Patient>): Promise<Patient> {
    const response = await authenticatedFetch(`${this.baseUrl}/api/patients`, {
      method: "POST",
      body: JSON.stringify(patient),
    });

    if (!response.ok) {
      throw new Error(`Failed to create patient: ${response.statusText}`);
    }

    return response.json();
  }

  async updatePatient(patientId: string, updates: Partial<Patient>): Promise<Patient> {
    const response = await authenticatedFetch(`${this.baseUrl}/api/patients/${patientId}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      throw new Error(`Failed to update patient: ${response.statusText}`);
    }

    return response.json();
  }

  async deletePatient(patientId: string): Promise<void> {
    const response = await authenticatedFetch(`${this.baseUrl}/api/patients/${patientId}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error(`Failed to delete patient: ${response.statusText}`);
    }
  }

  searchPatients(query: string, patients: Patient[]): Patient[] {
    if (!query.trim()) {
      return patients;
    }

    const lowerQuery = query.toLowerCase();

    return patients.filter(
      (patient) =>
        patient.patientName.toLowerCase().includes(lowerQuery) ||
        patient.room.roomNumber.toLowerCase().includes(lowerQuery) ||
        patient.bed.bedNumber.toLowerCase().includes(lowerQuery) ||
        patient.primaryDiagnosis.toLowerCase().includes(lowerQuery) ||
        patient.reason.toLowerCase().includes(lowerQuery)
    );
  }

  sortPatients(patients: Patient[], column: string | null, direction: "ASC" | "DESC" | null): Patient[] {
    if (!column || !direction) {
      return patients;
    }

    const sorted = [...patients];

    sorted.sort((a, b) => {
      let aValue: string | number | Date | undefined;
      let bValue: string | number | Date | undefined;

      switch (column) {
        case "reason":
          aValue = a.reason;
          bValue = b.reason;
          break;
        case "status":
          aValue = a.status;
          bValue = b.status;
          break;
        case "diagnosis":
          aValue = a.primaryDiagnosis;
          bValue = b.primaryDiagnosis;
          break;
        case "room":
          aValue = `${a.room.roomNumber}${a.bed.bedNumber}`;
          bValue = `${b.room.roomNumber}${b.bed.bedNumber}`;
          break;
        default:
          return 0;
      }

      if (aValue === undefined && bValue === undefined) return 0;
      if (aValue === undefined) return 1;
      if (bValue === undefined) return -1;

      const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      return direction === "ASC" ? comparison : -comparison;
    });

    return sorted;
  }
}

export const patientApiService = new PatientApiService();
